import { useState, useRef, useCallback, ChangeEvent } from "react";
import DateInput from "src/components/DateInput";
import FileInput from "src/components/FileInput";
import Input from "src/components/Input";
import Toggle from "src/components/Toggle";

interface FormInputProps<T>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  name: keyof T & string;
}
interface FormDateInputProps<T>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  name: keyof T & string;
}
interface FormTextAreaProps<T>
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "name"> {
  name: keyof T & string;
}
interface FormSelectProps<T>
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "name"> {
  name: keyof T & string;
}
interface FormOptionProps<T>
  extends Omit<React.OptionHTMLAttributes<HTMLOptionElement>, "name"> {
  name?: keyof T & string;
}
interface FormFileInputProps<T>
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "name" | "onChange"
  > {
  name: keyof T & string;
  onUploadProgress?: (progress: number, fileName: string) => void;
}
interface FormToggleProps<T> {
  name: keyof T & string;
  falseLabel: string;
  falseValue: string | number | boolean;
  trueLabel: string;
  trueValue: string | number | boolean;
  className?: string;
}

export function useForm<T extends Record<string, any>>(
  initialState: T,
  existingData?: T[] | undefined
) {
  const [formData, setFormData] = useState<T>(initialState);
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = useCallback(
    (
      e:
        | Event
        | ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
          >
    ) => {
      const name = (e.target as HTMLInputElement).name as keyof T;
      let value: string | number = (e.target as HTMLInputElement).value;
      if (isNaN(Number(value))) {
        value = String(value);
      } else if (typeof value === "string") {
        value = Number(value);
      }
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    },
    []
  );

  const resetForm = useCallback(() => {
    setFormData(initialState);
  }, [initialState]);

  const FormInput = useCallback(
    ({ ...props }: FormInputProps<T>) => {
      return (
        <Input {...props} onChange={handleChange} existingData={existingData} />
      );
    },
    [existingData]
  );

  const Select = useCallback(
    ({
      name,
      ...props
    }: FormSelectProps<T> & { children: React.ReactNode }) => {
      return (
        <select
          name={name}
          {...props}
          onChange={handleChange}
          className={`p-2 border-2 border-Grey9 text-Grey7 font-medium rounded-lg ${
            props.className || ""
          }`}
        >
          {props.children}
        </select>
      );
    },
    []
  );

  const Option = useCallback(({ name, ...props }: FormOptionProps<T>) => {
    return <option {...props} />;
  }, []);

  const TextArea = useCallback(({ name, ...props }: FormTextAreaProps<T>) => {
    const element = useRef<HTMLTextAreaElement>(null);
    const [currentValue, setCurrentValue] = useState("");
    return (
      <div className="flex flex-col gap-2 relative">
        <textarea
          ref={element}
          name={name}
          {...props}
          className={`p-2 border-2 border-Grey4  font-medium rounded ${
            props.className || ""
          }`}
          onChange={(e) => {
            setCurrentValue(e.target.value);
            handleChange(e);
          }}
        />
        <button
          onClick={(e) => {
            if (element.current) {
              element.current.value = "";
              setCurrentValue("");
            }
            setCurrentValue("");
          }}
          type="button"
          className={`absolute right-2 top-2 text-Grey7 hover:text-Grey4 transition-colors ${
            currentValue === "" ? "hidden" : ""
          }`}
          aria-label="Clear"
        >
          ✕
        </button>
      </div>
    );
  }, []);

  const FormDateInput = useCallback(
    ({
      name,
      defaultValue,
      ...props
    }: Omit<FormDateInputProps<T>, "defaultValue"> & {
      defaultValue?: Date;
    }) => {
      return (
        <DateInput
          name={name}
          defaultValue={defaultValue as Date}
          onChange={(val) => {
            setFormData((prev) => ({
              ...prev,
              [name]: new Date(Date.parse(val.target.value)),
            }));
          }}
        />
      );
    },
    []
  );

  const FormFileInput = useCallback(
    ({ name, onUploadProgress, ...props }: FormFileInputProps<T>) => (
      <FileInput
        required={!!props.required}
        name={name}
        {...props}
        onChange={(files) =>
          setFormData((prev) => ({
            ...prev,
            [name]: files,
          }))
        }
        onUploadProgress={onUploadProgress}
      />
    ),
    []
  );

  const FormToggle = useCallback(
    ({
      name,
      falseLabel,
      falseValue,
      trueLabel,
      trueValue,
      className,
    }: FormToggleProps<T>) => {
      const currentValue = formData[name];
      const defaultValue =
        currentValue === trueValue ? trueValue : falseValue;
      return (
        <Toggle
          name={name}
          falseLabel={falseLabel}
          falseValue={falseValue}
          trueLabel={trueLabel}
          trueValue={trueValue}
          defaultValue={defaultValue}
          className={className}
          onChange={(e) => {
            const newValue = e.target.checked ? trueValue : falseValue;
            setFormData((prev) => ({
              ...prev,
              [name]: newValue,
            }));
          }}
        />
      );
    },
    [formData]
  );

  const Form = useCallback(
    ({
      children,
      ...props
    }: {
      children: React.ReactNode;
    } & React.FormHTMLAttributes<HTMLFormElement>) => {
      return (
        <form ref={formRef} {...props}>
          {children}
        </form>
      );
    },
    []
  ) as React.FC<
    {
      children: React.ReactNode;
    } & React.FormHTMLAttributes<HTMLFormElement>
  > & {
    TextArea: typeof TextArea;
    Input: typeof Input;
    Select: typeof Select & { Option: typeof Option };
    Data: T;
    DateInput: typeof FormDateInput;
    FileInput: typeof FormFileInput;
    Toggle: typeof FormToggle;
  };

  // Attach components to Form
  (Form as any).TextArea = TextArea;
  (Form as any).Input = FormInput;
  (Select as any).Option = Option;
  (Form as any).Select = Select;
  (Form as any).Data = formData;
  (Form as any).DateInput = FormDateInput;
  (Form as any).FileInput = FormFileInput;
  (Form as any).Toggle = FormToggle;

  return {
    formData,
    resetForm,
    Form,
    setFormData,
    formRef,
  };
}
